import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { Elements } from '@stripe/react-stripe-js';

import { useCreateStripeOrderPaymentIntentMutation } from '../../../Account/graphql';
import { currentTransState } from '../../../atoms';
import { Error } from '../../../components/Error';
import { Loading } from '../../../components/Loading';
import { ErrorMessage } from '../../../GlobalComponents/ErrorMessage';
import { Flex } from '../../../GlobalComponents/Flex';
import { useAppSelector } from '../../../reduxstore/hooks';
import { storeIdState } from '../../../Store/atoms';
import { StoreFieldsFragment } from '../../../Store/graphql';
import { getStoreOpenStatus } from '../../../Store/utils/getStoreOpenStatus';
import { stripe as stripePromise } from '../../../stripe';
import { defined } from '../../../utils/defined';
import { getNow } from '../../../utils/getNow';
import { moneyToCents } from '../../../utils/moneyToCents';

import {
	checkoutErrorsState,
	checkoutState,
	isCheckoutLoadingState,
	sendOrderErrorState
} from '../../atoms';
import { Continue } from '../../components/Continue';
import GiftCardPayment from '../../components/GiftCardPayment';
import { Heading } from '../../components/Heading';
import { OrderInfo } from '../../components/OrderInfo';
import { SendOrderError } from '../../hooks/useSendOrder';
import { createCustomerOrderInput } from '../../utils/createCustomerOrderInput';
import { PaymentGateway, PaymentIntentDetails } from '../PaymentGateway';

export type StoreProps = React.HTMLAttributes<HTMLDivElement> & {
	store: StoreFieldsFragment;
	now?: DateTime;
};



export const Payment: React.FC<StoreProps> = ({ store, now = getNow() }) => {
	const navigate = useNavigate();

	const setSendOrderError = useSetRecoilState(sendOrderErrorState);
	const checkoutErrors = useRecoilValue(checkoutErrorsState);
	const state = useRecoilValue(checkoutState);
	const currentTrans = useRecoilValue(currentTransState);
	const loading = useRecoilValue(isCheckoutLoadingState);

	const [paymentDetails, setPaymentDetails] = useState<PaymentIntentDetails>({
		clientSecret: undefined,
		paymentIntentId: undefined,
		id: undefined
	});

	const [isLoading, setIsLoading] = useState(false);

	const [paymentIntentError, setPaymentIntentError] = useState<string>('');

	const profileIdFromRedux = useAppSelector(
		state => state.accountReducer.profile.id
	) as unknown as number;
	const appUserIdFromRedux = useAppSelector(state => state.authReducer.id);

	const guestName = useAppSelector(state => state.checkoutReducer.firstName);
	const guestPhone = useAppSelector(state => state.checkoutReducer.phone);
	const guestEmail = useAppSelector(state => state.checkoutReducer.email);
	const balanceDue = useAppSelector(
		state => state.checkoutReducer.balanceDue
	);

	const location = useLocation();
	const paymentType = location.state?.type || 'STRIPE';

	const resetRecoil = useResetRecoilState(storeIdState);

	const [paymentIntent] = useCreateStripeOrderPaymentIntentMutation({
		errorPolicy: 'none'
	});

	const getStripe = useCallback(() => {
		if (!currentTrans || !state || paymentType === 'GIFT_CARD') {
			return;
		}

		const appUserId = appUserIdFromRedux && +appUserIdFromRedux;
		const profileId =
			typeof profileIdFromRedux === 'number'
				? profileIdFromRedux
				: undefined;

		state.pickupName = guestName || '';
		state.guestEmail = guestEmail || '';
		state.phone = guestPhone || '';

		state.giftCardPayments = [];
		state.balanceDue = balanceDue;

		setIsLoading(true);
		paymentIntent({
			variables: {
				currency: 'usd',
				amount: moneyToCents(`${balanceDue}`),
				storeId: store.storeId,
				order: createCustomerOrderInput(
					state,
					currentTrans,
					undefined,
					profileId,
					appUserId
				)
			}
		})
			.then(res => {
				setPaymentDetails({
					clientSecret: res?.data?.createStripeOrderPaymentIntent.clientSecret,
					paymentIntentId: res?.data?.createStripeOrderPaymentIntent.paymentIntentId,
					id: res?.data?.createStripeOrderPaymentIntent.id
				});				
				setPaymentIntentError('');
			})
			.catch(err => {
				setPaymentIntentError(err.message);
			})
			.finally(() => {
				setIsLoading(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		appUserIdFromRedux,
		balanceDue,
		// currentTrans,
		guestEmail,
		guestName,
		guestPhone,
		paymentIntent,
		paymentType,
		profileIdFromRedux,
		// state,
		store.storeId
	]);

	useEffect(() => {
		getStripe();
	}, [balanceDue, getStripe]);

	const openStatus = useMemo(
		() => getStoreOpenStatus(store, now),
		[store, now]
	);

	const handleSendOrder = (): boolean => {
		const status = openStatus;
		if (status === 'CLOSED' || status === 'CLOSING_SOON') {
			return false;
		} else {
			return true;
		}
	};

	const HandleChoosePickup = () => {
		resetRecoil();
		navigate('../pickup');
	};

	return (
		<>
			{checkoutErrors.filter(defined).map(checkoutError => (
				<Error
					error={checkoutError.message}
					p={0}
					noBack
					onDismiss={() => {
						if (checkoutError instanceof SendOrderError) {
							setSendOrderError(undefined);
						}
					}}
				/>
			))}

			<div className="checkout-payment w-100">
				<OrderInfo />
				<Flex
					$flexDirection="column"
					$height="fit-content"
					$padding="16px 0 0 0"
					style={{
						borderTop: '1px solid #d9d9d9'
					}}
				>
					{handleSendOrder() === false && (
						<>
							<p>
								This store is currently not accepting online
								orders. Please select a different store.
							</p>
							<Continue
								loading={loading}
								onClick={HandleChoosePickup}
								continueLabel="Choose Store"
							/>
						</>
					)}

					{handleSendOrder() === true && (
						<>
							<Heading $fontSize="24px">Payment</Heading>

							{paymentType === 'GIFT_CARD' && (
								<>
									<Heading>
										Select BIGGBY Card to pay with:
									</Heading>
									<GiftCardPayment />
									{paymentType === 'GIFT_CARD' && (
										<Heading style={{ marginTop: '24px' }}>
											Balance Due: ${balanceDue}
										</Heading>
									)}
									<PaymentGateway paymentType={paymentType} />
								</>
							)}

							{paymentIntentError && (
								<ErrorMessage message={paymentIntentError} />
							)}

							{!paymentIntentError && (
								<>
									{paymentType === 'STRIPE' && (
										<>
											{paymentDetails.clientSecret && !isLoading ? (
												<>
													<Elements
														options={{
															clientSecret: paymentDetails.clientSecret,
															appearance: {
																variables: {
																	iconColor:
																		'#f47920',
																	colorPrimary:
																		'#f47920'
																}
															}
														}}
														stripe={stripePromise}
													>
														<PaymentGateway
															paymentType={
																paymentType
															}
															paymentDetails={
																paymentDetails
															}
														/>
													</Elements>
												</>
											) : (
												<Loading />
											)}
										</>
									)}
								</>
							)}
						</>
					)}
				</Flex>
			</div>
		</>
	);
};
